import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { cardsAPI } from '../../../api/modules/cards.module';
import { EVENTS } from '../../../analytics/events';
import { track, trackFirstTime } from '../../../analytics/track';
import { useAuth } from '../../../hooks/useAuth';
import { useCart } from '../../../Contexts/CartContext';
import { useFavourites } from '../../../favourites/useFavourites';
/* The shared in-flight dedupe for `refresh-card-prices`, not the hook itself --
   this page still owns its cart write (see `handleAddToCart` below). */
import { refreshCardPricesOnce } from '../../../hooks/useAddToCart';
import { showToast } from '../../../utils/toast';
import {
  isPriceUnavailable,
  PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE,
} from '../../../utils/priceAvailability';
import { mediaUrl } from '../../../utils/common';
import { transformListDetailsResponse } from '../../../utils/transmations';
import ShareModal from '../../UI/ShareModal/ShareModal';
import { ListingDetailView } from './listing.view';
import { useFulfilmentMode } from '../../../hooks/useFulfilmentMode';

/**
 * ONE SELLER'S LISTING, the container. `/marketplace/card/:cardId/:listId`.
 *
 * Owns the fetch, the favourites round trip, the cart write and the analytics.
 * Everything visual is in `listing.view.jsx`, which is pure, so the state
 * explorer mounts the real screen rather than a mock of it.
 *
 * WHY THIS FILE IS SO MUCH SMALLER THAN IT WAS
 * --------------------------------------------
 * The page used to carry its own gallery, its own info block, its own seller
 * card, its own chart wrapper, its own recommendations slider and a duplicate
 * listings table, each with a stylesheet using the SAME global class names as
 * the catalog page's copies. Whichever stylesheet loaded last styled both
 * pages. All of that is gone; the shared shapes live in `patterns/detail` and
 * the related rails are the shared `ListingCard`.
 *
 * THE `$` BUG THIS BATCH WAS SENT TO KILL
 * ---------------------------------------
 * `components/CardDetailListings/CardDetailListings.jsx` rendered
 * `${formatNumber(listing.price, 2)}` and `+ ${...} Shipping` on Rand amounts.
 * It was also unreachable dead code, imported by nothing. It is deleted, and
 * every price on both pages now goes through `formatCurrency`, which prefixes
 * `R` and cannot emit a dollar sign.
 */

export default function ListDetail() {
  const fulfilmentMode = useFulfilmentMode();
  const { cardId, listId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { cartItems, setCartItems } = useCart();
  const favourites = useFavourites();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const cartKey = user ? `cart_${user.id || user._id}` : 'cart_guest';
  const cardHref = cardId ? `/marketplace/card/${cardId}` : null;

  useEffect(() => {
    let cancelled = false;

    async function fetchListing() {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const response = await cardsAPI.getListDetails(listId);
        if (cancelled) return;

        const transformed = transformListDetailsResponse(response);
        if (!transformed) {
          setNotFound(true);
          return;
        }

        setData(transformed);

        track(EVENTS.LISTING_VIEWED, {
          listing_id: transformed?.id,
          card_name: transformed?.name,
          price: transformed?.price,
          condition: transformed?.condition,
          seller_id: transformed?.seller?.id,
          seller_name: transformed?.seller?.store_name,
          set_name: transformed?.listing?.set_name,
          quantity: transformed?.listing?.quantity,
        });
      } catch (requestError) {
        if (cancelled) return;
        console.error('Error fetching listing:', requestError);
        if (requestError?.response?.status === 404) setNotFound(true);
        else setError('Something went wrong loading this listing. Try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (listId) fetchListing();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, user]);

  const listing = data?.listing;

  /* THE PRICE REFRESH, FIRED AFTER PAINT (issue #110).
     ---------------------------------------------------
     `handleAddToCart` below blocks a stale TRACKED listing, and until this
     effect existed NOTHING ON THIS PAGE COULD UNBLOCK ONE. A buyer deep-linked
     straight to `/marketplace/card/:cardId/:listId` -- a shared link, a search
     result -- saw a refusal they could only clear by navigating to the catalog
     card page, which is the one surface that refreshes and which they have no
     reason to know exists. The block was correct and the dead end was total.

     Same endpoint, same shape and same rules as the catalog card page's copy
     (`Marketplace/CardDetail/CardDetail.jsx`) -- read that one's comment for
     why the work is split off the render path at all. Three differences, all
     from this page being about ONE listing rather than a card:

     - It waits on THIS listing having settled (`data.id === listId`), not on a
       card id. Same reason the other page checks its card: on a listing ->
       listing navigation the new render still carries the PREVIOUS listing's
       `loading === false`, and firing then would refresh against the old page
       and pay the server cost twice.
     - It patches from `listed_cards`, this listing's own row, which comes back
       through `CardListingWithSellerSerializer` and so carries a freshly
       computed `price_stale` -- the whole point of the trip -- plus whatever
       `reprice_card_listings` just did to `price`.
     - A row that is ABSENT is left alone rather than treated as an answer.
       `listed_cards` only holds approved, in-stock, undeleted listings, so a
       miss means "not buyable right now", which is a real fact but not one
       this patch is shaped to express: blanking `price_stale` would silently
       UNBLOCK the listing, the exact inversion of what this effect is for.
       The buyer keeps the page they can already see, the stale block stands,
       and `create_order_and_payment` stays the guarantee either way.

     AND IT GOES THROUGH `refreshCardPricesOnce` (final review, must-fix 1).
     This page does not use `useAddToCart` today, so nothing here races it --
     but the dedupe is a property of the ENDPOINT, not of the hook: the server
     takes a non-blocking `pg_try_advisory_lock`, so any second concurrent call
     for the same card returns the pre-refresh state and spends a throttle slot
     saying nothing. Routing every caller through the one in-flight map is what
     keeps that true as this page grows a second one; see that function's
     header in `hooks/useAddToCart.js`. */
  useEffect(() => {
    if (!cardId || loading || notFound || error) return;
    if (!data || String(data.id) !== String(listId)) return;

    let cancelled = false;

    refreshCardPricesOnce(cardId)
      .then((response) => {
        if (cancelled || !response) return;

        const fresh = (response.listed_cards || []).find(
          (row) => String(row.id) === String(listId)
        );
        if (!fresh) return;

        setData((prev) =>
          prev && String(prev.id) === String(listId)
            ? {
                ...prev,
                listing: { ...prev.listing, ...fresh },
                /* `transformListDetailsResponse` derives this off
                   `listing.price`, and the view renders IT, not the listing --
                   so a repriced listing whose derived copy stayed behind would
                   show one number and sell at another. */
                price: Number(fresh.price) || 0,
              }
            : prev
        );
      })
      .catch((refreshError) => {
        /* Swallowed on purpose: the page is already fully rendered from the
           cached read above, and a failed refresh must never take away what
           the buyer can see. Skip the log once cancelled -- an abandoned
           refresh failing after the fact is not news. */
        if (cancelled) return;
        console.error('Error refreshing card prices:', refreshError);
      });

    return () => {
      cancelled = true;
    };
    /* `data?.id` and not `data`: the id is what decides whether this effect may
       run, and it is a primitive that the success handler above leaves alone --
       so the patch cannot re-trigger the effect that produced it, while a
       genuinely new listing arriving still does. Depending on the whole object
       would loop; depending on neither would MISS the arrival if `setData` and
       `setLoading(false)` ever landed in separate renders. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, listId, loading, notFound, error, data?.id]);

  /* THE CATALOG CARD ID, NOT `listing.product`.
     -------------------------------------------
     `listing.product` on this payload is `CardListing.product_id` -- the FK to
     the legacy `CollectrProduct` table (cards/models.py). It is NOT the
     `"product": str(fav.card_id)` alias `get_favourites` emits, despite the
     matching name, and the live listing-creation path sets `card=` and never
     `product` (cards/views.py), so on every modern listing it is null. The
     heart therefore never filled and tapping it reported "We could not
     identify this card".
     `card_id` is a SerializerMethodField on `CardListingWithSellerSerializer`
     returning `str(obj.card_id)`, which is exactly the id the favourites
     endpoints key on and exactly what `useFavourites` holds. The `:cardId`
     route param is the same value (this page is only ever reached from
     `/marketplace/card/${cardId}/${listingId}`) and stands in while the
     listing is still loading. */
  const savedCardId = listing?.card_id || cardId;

  /* No local `saved` state: it drifted from reality the moment a guest saved
     here and then looked at the browse grid, or vice versa. `useFavourites`
     is the one source of truth for every surface; this page just reads it. */
  const saved = favourites.isSaved(savedCardId);

  /** How much of THIS listing is already in the cart, across every seller key. */
  const inCartQuantity = useMemo(() => {
    let total = 0;
    Object.values(cartItems || {}).forEach((items) => {
      (items || []).forEach((item) => {
        if (String(item.listing_id) === String(data?.id)) total += item.quantity || 0;
      });
    });
    return total;
  }, [cartItems, data?.id]);

  /* NO LOGIN GATE. A signed-out buyer used to be toasted and thrown to /login,
     losing the listing they were reading and landing somewhere else
     afterwards. `useFavourites` saves to the guest list instead, and the
     merge on sign-in picks it up. */
  const handleToggleSave = async () => {
    if (saving) return;

    if (!savedCardId) {
      showToast.error('We could not identify this card');
      return;
    }

    const wasSaved = saved;
    setSaving(true);
    try {
      await favourites.toggle(savedCardId);
      if (!wasSaved) {
        track(EVENTS.COLLECTION_ITEM_ADDED, { card_id: savedCardId });
        trackFirstTime('first_collection_item_added', EVENTS.FIRST_COLLECTION_ITEM_ADDED, {
          card_id: savedCardId,
        });
        showToast.success('Card added to your collection');
      } else {
        showToast.success('Card removed from your collection');
      }
    } catch (requestError) {
      /* The CATCH stays, the toast does not -- `useFavourites` reports the
         failure itself now, so toasting here would say it twice. This block
         still earns its place by keeping the success toast and the analytics
         event below the await from firing for a save that did not happen. */
      console.error('Error updating saved status:', requestError);
    } finally {
      setSaving(false);
    }
  };

  /* SECOND CART-WRITE PATH, OUTSIDE `useAddToCart` -- HAS THE GUARD, NOT
     THE SHARED WRITE PATH.
     `useAddToCart` (`hooks/useAddToCart.js`) is documented as the single
     cart write, and its own docstring used to claim it was "lifted verbatim
     from CardDetail.jsx, which owned the only copy" -- that claim is false.
     This file is `/marketplace/card/:cardId/:listId`, a SEPARATE page from
     the one that docstring meant (`Marketplace/CardDetail/CardDetail.jsx`,
     the catalog card page), and it never migrated: this function still
     duplicates the localStorage read/write/seller-bucket/stock-clamp logic
     inline below, instead of calling the hook.

     WHAT WAS FIXED (review round 2, 2026-08-29): this page's `listing` (from
     `card-listing-detail/`, via `CardListingWithSellerSerializer`) already
     carried `pricing_mode`/`price_stale` -- they were just never read. That
     made the stale-tracked-price guard a small, single-file addition rather
     than the multi-file data-threading job round 1 assumed, so the guard
     below now calls the SAME shared predicate the other surfaces use
     (`isPriceUnavailable` in `utils/priceAvailability.js`) with the SAME
     copy, rather than writing a second, divergent copy of the condition.

     WHAT IS STILL DEFERRED: the write path itself. This function still
     duplicates `useAddToCart`'s localStorage read/write/seller-bucket/
     stock-clamp logic instead of calling the hook. Consolidating the two
     cart-write paths remains out of scope -- a real refactor of a live
     checkout-adjacent surface, needing its own tests and review, not
     something to bolt onto the end of an unrelated task.

     THE GUARANTEE WAS ALWAYS UNAFFECTED EITHER WAY. Checkout re-reads
     `price_stale` fresh from `listings-by-ids/` regardless of which page
     originally added the row (see `Checkout.jsx`'s `isCheckoutItemBlocked`),
     and `POST /api/orders/create-order/` still 400s it server-side. Before
     this fix that was the ONLY protection this page had; now it also has
     the same click-saving affordance the other two surfaces have. */
  const handleAddToCart = (requested = 1) => {
    if (isPriceUnavailable(listing)) {
      showToast.error(PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE);
      return;
    }

    if (!listing || listing.quantity === 0) {
      showToast.error('This item is sold out');
      return;
    }

    const sellerId =
      typeof listing.seller === 'object'
        ? listing.seller?.id?.toString()
        : listing.seller_id?.toString();

    if (!sellerId) {
      showToast.error('Seller information is missing');
      return;
    }

    setAdding(true);
    try {
      const cart = JSON.parse(localStorage.getItem(cartKey)) || {};
      if (!cart[sellerId]) cart[sellerId] = [];

      const index = cart[sellerId].findIndex((item) => item.listing_id === listing.id);
      const existing = index >= 0 ? cart[sellerId][index].quantity || 0 : 0;
      const remaining = listing.quantity - existing;

      if (remaining <= 0) {
        showToast.error('Maximum quantity already added for this item');
        return;
      }

      const toAdd = Math.min(requested, remaining);
      if (index >= 0) cart[sellerId][index].quantity = existing + toAdd;
      else cart[sellerId].push({ listing_id: listing.id, quantity: toAdd });

      localStorage.setItem(cartKey, JSON.stringify(cart));
      setCartItems(cart);

      if (toAdd < requested) {
        showToast.info(`Only ${remaining} available; added ${toAdd}.`);
        return;
      }

      showToast.success('Added to cart');
      track(EVENTS.ADD_TO_CART_CLICKED, {
        listing_id: listing.id,
        price: data?.price,
        quantity: toAdd,
        seller_id: sellerId,
      });

      const totalItems = Object.values(cart).reduce(
        (sum, items) => sum + items.reduce((inner, item) => inner + (item.quantity || 0), 0),
        0
      );
      const payload = {
        cart_id: cartKey,
        item_count: totalItems,
        seller_count: Object.keys(cart).length,
      };
      const wasEmpty = totalItems - toAdd <= 0;
      track(wasEmpty ? EVENTS.CART_CREATED : EVENTS.CART_UPDATED, payload);
      if (wasEmpty) trackFirstTime('first_cart_created', EVENTS.FIRST_CART_CREATED, payload);
    } finally {
      setAdding(false);
    }
  };

  const viewListing = data
    ? {
        id: data.id,
        name: data.name,
        set: listing?.set_name,
        rarity: listing?.rarity,
        condition: data.condition,
        /* Sent on this payload and dropped here until now — the listing page
           could not name the printing it was selling. */
        finish: data.finish ?? listing?.finish,
        cardNumber: listing?.card_number,
        price: data.price,
        marketPrice: listing?.market_price ?? listing?.latest_price,
        quantity: listing?.quantity ?? 0,
        processingDays: listing?.processing_days,
        description: data.description,
        images: (data.images || []).map(mediaUrl).filter(Boolean),
        cardHref,
      }
    : // Even with no listing in hand we still know which CARD the URL was
      // about, so the not-found state can offer the useful next step, every
      // other seller with this card, rather than dumping the buyer at the top
      // of the marketplace.
      { cardHref };

  const viewSeller = data
    ? {
        id: data.seller?.id,
        name: data.seller?.name,
        storeName: data.seller?.store_name,
        avatar: data.seller?.avatar,
        // `transformListDetailsResponse` stores the rating as a 0-100
        // percentage, so it comes back to a 5 point scale here. Exactly one
        // conversion, in one place, feeding the one shared `Rating`.
        rating: data.seller?.rating ? Number(data.seller.rating) / 20 : null,
        sales: data.seller?.sales,
        verified: Boolean(listing?.seller?.is_verified),
      }
    : {};

  return (
    <ListingDetailView
      /* The buy box may only promise delivery when we positively know
         Sleevr is shipping. 'unknown' — a signed-out visitor, whose
         session cannot read the settings endpoint — says nothing. */
      shipsNationwide={fulfilmentMode === 'shipping'}
      loading={loading}
      error={error}
      notFound={notFound}
      listing={viewListing}
      seller={viewSeller}
      priceHistory={listing?.price_history}
      quantity={quantity}
      onQuantityChange={setQuantity}
      inCartQuantity={inCartQuantity}
      adding={adding}
      onAddToCart={handleAddToCart}
      saved={saved}
      saving={saving}
      onToggleSave={handleToggleSave}
      onShare={() => setShareOpen(true)}
      shareModal={
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          title={`Share ${data?.name || 'this card'}`}
          description={`Checkout this ${data?.name || 'card'} on Sleevr!`}
          image={viewListing.images?.[0] || null}
        />
      }
      sellerListings={(data?.sellerOtherListings || []).map((item) => ({
        id: item.id,
        href: `/marketplace/card/${item.card_id || cardId}/${item.id}`,
        name: item.name,
        set: item.set,
        rarity: item.rarity,
        condition: item.condition,
        price: item.price,
        image: mediaUrl(item.image),
      }))}
      related={(data?.recommendations || []).map((item) => ({
        id: item.id,
        href: item.card_id ? `/marketplace/card/${item.card_id}` : `/marketplace/card/${item.id}`,
        name: item.name,
        set: item.set,
        rarity: item.rarity,
        price: item.price,
        marketPrice: item.marketPrice,
        image: mediaUrl(item.image),
      }))}
      /* Passed through untouched, and only ever back UP. The card page above
         put it here on the way down (see `CardDetail/CardDetail.jsx`) so that
         climbing out of this listing returns to a card that still knows which
         results it was opened from. Absent on a deep link or a refresh, and
         then every control up the trail falls back to its crumb, as before. */
      backState={location.state}
      /* No `onCartClick`: the shell's default opens the cart DRAWER, which is
         the quick view. Overriding it here made these two pages the only ones
         where the cart navigated away instead. */
    />
  );
}
